"use client";

import { c as _c } from "react/compiler-runtime";
import MediaTypeBadge from "@/components/drops/media/MediaTypeBadge";
import DropListItemContentMedia from "@/components/drops/view/item/content/media/DropListItemContentMedia";
import CommonDropdownItemsMobileWrapper from "@/components/utils/select/dropdown/CommonDropdownItemsMobileWrapper";
import { MobileVotingModal, VotingModal } from "@/components/voting";
import VotingModalButton from "@/components/voting/VotingModalButton";
import { DropLocation } from "@/components/waves/drops/Drop";
import WaveDropActionsOpen from "@/components/waves/drops/WaveDropActionsOpen";
import WaveDropActionsOptions from "@/components/waves/drops/WaveDropActionsOptions";
import WaveDropMobileMenuDelete from "@/components/waves/drops/WaveDropMobileMenuDelete";
import WaveDropMobileMenuCopyLink from "@/components/waves/drops/WaveDropMobileMenuCopyLink";
import WaveDropMobileMenuOpen from "@/components/waves/drops/WaveDropMobileMenuOpen";
import MemesArtSubmissionModal from "@/components/waves/memes/MemesArtSubmissionModal";
import { MemesArtResubmitAction } from "@/components/waves/memes/submission/MemesArtResubmitAction";
import type { ApiWave } from "@/generated/models/ApiWave";
import { formatNumberWithCommas } from "@/helpers/Helpers";
import { ImageScale } from "@/helpers/image.helpers";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { useDropInteractionRules } from "@/hooks/drops/useDropInteractionRules";
import useIsMobileScreen from "@/hooks/isMobileScreen";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import useLongPressInteraction from "@/hooks/useLongPressInteraction";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { startDropOpen } from "@/utils/monitoring/dropOpenTiming";
import Link from "next/link";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import MemeDropTraits from "./MemeDropTraits";
import MemesLeaderboardDropArtistInfo from "./MemesLeaderboardDropArtistInfo";
import MemesLeaderboardDropCard from "./MemesLeaderboardDropCard";
import MemesLeaderboardDropDescription from "./MemesLeaderboardDropDescription";
import MemesLeaderboardDropHeader from "./MemesLeaderboardDropHeader";
import MemesLeaderboardDropVoteSummary from "./MemesLeaderboardDropVoteSummary";
interface MemesLeaderboardDropProps {
  readonly drop: ExtendedDrop;
  readonly onDropClick: (drop: ExtendedDrop) => void;
  readonly wave?: ApiWave | undefined;
  readonly location?: DropLocation | undefined;
  readonly onSourceDropDeleted?: (() => void) | undefined;
}
const getNonEmptyText = (value: string | null | undefined): string | null => {
  const text = value?.trim();
  return text && text.length > 0 ? text : null;
};
const getMetadataValue = (drop: ExtendedDrop, dataKey: string): string | null => getNonEmptyText(drop.metadata.find(metadata => metadata.data_key === dataKey)?.data_value);
export const MemesLeaderboardDrop: React.FC<MemesLeaderboardDropProps> = t0 => {
  const $ = _c(121);
  const {
    drop,
    onDropClick,
    wave,
    location: t1,
    onSourceDropDeleted
  } = t0;
  const location = t1 === undefined ? DropLocation.WAVE : t1;
  const isMobileScreen = useIsMobileScreen();
  const isTabletOrSmaller = useMediaQuery("(max-width: 1023px)");
  const {
    canDelete
  } = useDropInteractionRules(drop);
  const [isVotingModalOpen, setIsVotingModalOpen] = useState(false);
  const [isResubmitModalOpen, setIsResubmitModalOpen] = useState(false);
  const openResubmitAfterMenuCloseRef = useRef(false);
  const {
    hasTouchScreen
  } = useDeviceInfo();
  let mediaImageScale = ImageScale.AUTOx800;
  if (isMobileScreen) {
    mediaImageScale = ImageScale.AUTOx450;
  } else {
    if (isTabletOrSmaller) {
      mediaImageScale = ImageScale.AUTOx600;
    }
  }
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      openResubmitAfterMenuCloseRef.current = false;
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const clearDeferredResubmitOpen = t2;
  let t3;
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => () => {
      clearDeferredResubmitOpen();
    };
    t4 = [clearDeferredResubmitOpen];
    $[1] = t3;
    $[2] = t4;
  } else {
    t3 = $[1];
    t4 = $[2];
  }
  useEffect(t3, t4);
  let t5;
  if ($[3] !== hasTouchScreen) {
    t5 = {
      hasTouchScreen,
      onInteractionStart: clearDeferredResubmitOpen
    };
    $[3] = hasTouchScreen;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const {
    isActive,
    setIsActive,
    touchHandlers
  } = useLongPressInteraction(t5);
  let t6;
  if ($[5] !== setIsActive) {
    t6 = () => {
      openResubmitAfterMenuCloseRef.current = true;
      setIsActive(false);
    };
    $[5] = setIsActive;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const openResubmitAfterMobileMenuCloses = t6;
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      if (!openResubmitAfterMenuCloseRef.current) {
        return;
      }
      clearDeferredResubmitOpen();
      setIsResubmitModalOpen(true);
    };
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const handleMobileMenuAfterLeave = t7;
  let description;
  let t8;
  let title;
  if ($[8] !== drop) {
    const firstPart = drop.parts.at(0);
    title = getNonEmptyText(drop.title) ?? getMetadataValue(drop, "title") ?? "Artwork Title";
    description = getNonEmptyText(firstPart?.content) ?? getMetadataValue(drop, "description") ?? "This is an artwork submission for The Memes collection.";
    t8 = firstPart?.media.at(0);
    $[8] = drop;
    $[9] = description;
    $[10] = t8;
    $[11] = title;
  } else {
    description = $[9];
    t8 = $[10];
    title = $[11];
  }
  const artworkMedia = t8;
  let t9;
  if ($[12] !== drop.top_raters) {
    t9 = drop.top_raters.slice(0, 3);
    $[12] = drop.top_raters;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const firstThreeVoters = t9;
  let t10;
  if ($[14] !== drop || $[15] !== hasTouchScreen || $[16] !== isMobileScreen || $[17] !== onDropClick) {
    t10 = () => {
      if (hasTouchScreen) {
        return;
      }
      startDropOpen({
        dropId: drop.id,
        waveId: drop.wave.id,
        source: "leaderboard_memes",
        isMobile: isMobileScreen
      });
      onDropClick(drop);
    };
    $[14] = drop;
    $[15] = hasTouchScreen;
    $[16] = isMobileScreen;
    $[17] = onDropClick;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== drop) {
    t11 = <MemesLeaderboardDropArtistInfo drop={drop} />;
    $[19] = drop;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== canDelete || $[22] !== drop || $[23] !== hasTouchScreen || $[24] !== onSourceDropDeleted || $[25] !== wave) {
    t12 = !hasTouchScreen && <><WaveDropActionsOpen drop={drop} /><MemesArtResubmitAction drop={drop} wave={wave} onSourceDropDeleted={onSourceDropDeleted} />{canDelete && <WaveDropActionsOptions drop={drop} />}</>;
    $[21] = canDelete;
    $[22] = drop;
    $[23] = hasTouchScreen;
    $[24] = onSourceDropDeleted;
    $[25] = wave;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t12) {
    t13 = <div className="tw-flex tw-gap-2 tw-text-iron-400">{t12}</div>;
    $[27] = t12;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t11 || $[30] !== t13) {
    t14 = <div className="tw-border-x-0 tw-border-b tw-border-t-0 tw-border-solid tw-border-white/5 tw-bg-iron-900/30 tw-p-4 tw-pb-3"><div className="tw-flex tw-items-start tw-justify-between tw-gap-4">{t11}{t13}</div></div>;
    $[29] = t11;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  const t15 = artworkMedia?.mime_type;
  let t16;
  if ($[32] !== drop.id || $[33] !== t15) {
    t16 = <MediaTypeBadge mimeType={t15} dropId={drop.id} size="sm" />;
    $[32] = drop.id;
    $[33] = t15;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== title) {
    t17 = <MemesLeaderboardDropHeader title={title} />;
    $[35] = title;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== t16 || $[38] !== t17) {
    t18 = <div className="tw-flex tw-items-center tw-gap-2">{t16}{t17}</div>;
    $[37] = t16;
    $[38] = t17;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== description) {
    t19 = <MemesLeaderboardDropDescription description={description} />;
    $[40] = description;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== t18 || $[43] !== t19) {
    t20 = <div className="tw-px-4 tw-pb-4 tw-pt-4"><div className="tw-space-y-1">{t18}{t19}</div></div>;
    $[42] = t18;
    $[43] = t19;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== artworkMedia || $[46] !== location || $[47] !== mediaImageScale) {
    t21 = artworkMedia && <div className={`tw-flex tw-h-96 tw-justify-center ${location === DropLocation.WAVE ? "tw-bg-iron-950" : "tw-bg-iron-900/40"}`}><DropListItemContentMedia media_mime_type={artworkMedia.mime_type} media_url={artworkMedia.url} isCompetitionDrop={true} imageScale={mediaImageScale} /></div>;
    $[45] = artworkMedia;
    $[46] = location;
    $[47] = mediaImageScale;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  let t22;
  if ($[49] !== drop) {
    t22 = <MemeDropTraits drop={drop} />;
    $[49] = drop;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  let t23;
  if ($[51] !== drop.context_profile_context || $[52] !== drop.raters_count || $[53] !== drop.rating || $[54] !== drop.rating_prediction || $[55] !== drop.wave.voting_credit_type || $[56] !== firstThreeVoters) {
    t23 = <MemesLeaderboardDropVoteSummary current={drop.rating} projected={drop.rating_prediction} creditType={drop.wave.voting_credit_type} ratersCount={drop.raters_count} topVoters={firstThreeVoters} userContext={drop.context_profile_context} />;
    $[51] = drop.context_profile_context;
    $[52] = drop.raters_count;
    $[53] = drop.rating;
    $[54] = drop.rating_prediction;
    $[55] = drop.wave.voting_credit_type;
    $[56] = firstThreeVoters;
    $[57] = t23;
  } else {
    t23 = $[57];
  }
  let t24;
  if ($[58] !== firstThreeVoters) {
    t24 = firstThreeVoters.map(_temp3);
    $[58] = firstThreeVoters;
    $[59] = t24;
  } else {
    t24 = $[59];
  }
  let t25;
  if ($[60] !== t24) {
    t25 = <div className="tw-flex tw-items-center -tw-space-x-2">{t24}</div>;
    $[60] = t24;
    $[61] = t25;
  } else {
    t25 = $[61];
  }
  let t26;
  if ($[62] !== drop.raters_count) {
    t26 = formatNumberWithCommas(drop.raters_count);
    $[62] = drop.raters_count;
    $[63] = t26;
  } else {
    t26 = $[63];
  }
  const t27 = drop.raters_count === 1 ? "voter" : "voters";
  let t28;
  if ($[64] !== t27) {
    t28 = <span className="tw-font-normal tw-text-iron-500">{t27}</span>;
    $[64] = t27;
    $[65] = t28;
  } else {
    t28 = $[65];
  }
  let t29;
  if ($[66] !== t26 || $[67] !== t28) {
    t29 = <span className="tw-text-xs tw-font-bold tw-text-white">{t26}{" "}{t28}</span>;
    $[66] = t26;
    $[67] = t28;
    $[68] = t29;
  } else {
    t29 = $[68];
  }
  let t30;
  if ($[69] !== t25 || $[70] !== t29) {
    t30 = <div className="tw-flex tw-items-center tw-gap-2 @[700px]:tw-hidden">{t25}{t29}</div>;
    $[69] = t25;
    $[70] = t29;
    $[71] = t30;
  } else {
    t30 = $[71];
  }
  let t31;
  if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = () => setIsVotingModalOpen(true);
    $[72] = t31;
  } else {
    t31 = $[72];
  }
  let t32;
  if ($[73] !== drop) {
    t32 = <VotingModalButton drop={drop} onClick={t31} />;
    $[73] = drop;
    $[74] = t32;
  } else {
    t32 = $[74];
  }
  let t33;
  if ($[75] !== t30 || $[76] !== t32) {
    t33 = <div className="tw-flex tw-w-full tw-flex-shrink-0 tw-items-center tw-justify-between tw-gap-4 @[700px]:tw-w-auto @[700px]:tw-justify-end" onClick={_temp}>{t30}{t32}</div>;
    $[75] = t30;
    $[76] = t32;
    $[77] = t33;
  } else {
    t33 = $[77];
  }
  let t34;
  if ($[78] !== t23 || $[79] !== t33) {
    t34 = <div className="tw-flex tw-flex-col tw-justify-between tw-gap-4 @[700px]:tw-flex-row @[700px]:tw-items-center">{t23}{t33}</div>;
    $[78] = t23;
    $[79] = t33;
    $[80] = t34;
  } else {
    t34 = $[80];
  }
  let t35;
  if ($[81] !== t22 || $[82] !== t34) {
    t35 = <div className="tw-mt-4 tw-flex tw-flex-col tw-gap-y-4 tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-white/5 tw-bg-iron-900/30 tw-p-4">{t22}{t34}</div>;
    $[81] = t22;
    $[82] = t34;
    $[83] = t35;
  } else {
    t35 = $[83];
  }
  let t36;
  if ($[84] !== t14 || $[85] !== t20 || $[86] !== t21 || $[87] !== t35) {
    t36 = <div>{t14}{t20}{t21}{t35}</div>;
    $[84] = t14;
    $[85] = t20;
    $[86] = t21;
    $[87] = t35;
    $[88] = t36;
  } else {
    t36 = $[88];
  }
  let t37;
  if ($[89] !== drop || $[90] !== isMobileScreen || $[91] !== isVotingModalOpen) {
    t37 = isMobileScreen ? <MobileVotingModal drop={drop} isOpen={isVotingModalOpen} onClose={() => setIsVotingModalOpen(false)} /> : <VotingModal drop={drop} isOpen={isVotingModalOpen} onClose={() => setIsVotingModalOpen(false)} />;
    $[89] = drop;
    $[90] = isMobileScreen;
    $[91] = isVotingModalOpen;
    $[92] = t37;
  } else {
    t37 = $[92];
  }
  let t38;
  if ($[93] !== drop || $[94] !== t36 || $[95] !== t37) {
    t38 = <MemesLeaderboardDropCard drop={drop}>{t36}{t37}</MemesLeaderboardDropCard>;
    $[93] = drop;
    $[94] = t36;
    $[95] = t37;
    $[96] = t38;
  } else {
    t38 = $[96];
  }
  let t39;
  if ($[97] !== t38 || $[98] !== touchHandlers) {
    t39 = <div {...touchHandlers}>{t38}</div>;
    $[97] = t38;
    $[98] = touchHandlers;
    $[99] = t39;
  } else {
    t39 = $[99];
  }
  let t40;
  if ($[100] !== canDelete || $[101] !== drop || $[102] !== hasTouchScreen || $[103] !== isActive || $[104] !== onSourceDropDeleted || $[105] !== openResubmitAfterMobileMenuCloses || $[106] !== setIsActive || $[107] !== wave) {
    t40 = hasTouchScreen && createPortal(<CommonDropdownItemsMobileWrapper isOpen={isActive} setOpen={setIsActive} onAfterLeave={handleMobileMenuAfterLeave}><div className="tw-grid tw-grid-cols-1 tw-gap-y-2"><WaveDropMobileMenuOpen drop={drop} onOpenChange={() => setIsActive(false)} /><WaveDropMobileMenuCopyLink drop={drop} onCopy={() => setIsActive(false)} /><MemesArtResubmitAction drop={drop} wave={wave} variant="menu" onOpenModal={openResubmitAfterMobileMenuCloses} onSourceDropDeleted={onSourceDropDeleted} />{canDelete && <WaveDropMobileMenuDelete drop={drop} onDropDeleted={() => setIsActive(false)} />}</div></CommonDropdownItemsMobileWrapper>, document.body);
    $[100] = canDelete;
    $[101] = drop;
    $[102] = hasTouchScreen;
    $[103] = isActive;
    $[104] = onSourceDropDeleted;
    $[105] = openResubmitAfterMobileMenuCloses;
    $[106] = setIsActive;
    $[107] = wave;
    $[108] = t40;
  } else {
    t40 = $[108];
  }
  let t41;
  if ($[109] !== drop || $[110] !== isResubmitModalOpen || $[111] !== onSourceDropDeleted || $[112] !== wave) {
    t41 = wave && isResubmitModalOpen && <MemesArtSubmissionModal isOpen={isResubmitModalOpen} wave={wave} sourceDrop={drop} onClose={() => setIsResubmitModalOpen(false)} onSourceDropDeleted={onSourceDropDeleted} />;
    $[109] = drop;
    $[110] = isResubmitModalOpen;
    $[111] = onSourceDropDeleted;
    $[112] = wave;
    $[113] = t41;
  } else {
    t41 = $[113];
  }
  let t42;
  if ($[114] !== t39 || $[115] !== t40 || $[116] !== t41) {
    t42 = <div className="tw-group tw-w-full">{t39}{t40}{t41}</div>;
    $[114] = t39;
    $[115] = t40;
    $[116] = t41;
    $[117] = t42;
  } else {
    t42 = $[117];
  }
  let t43;
  if ($[118] !== t10 || $[119] !== t42) {
    t43 = <div className="tw-w-full tw-cursor-pointer tw-@container" onClick={t10}>{t42}</div>;
    $[118] = t10;
    $[119] = t42;
    $[120] = t43;
  } else {
    t43 = $[120];
  }
  return t43;
};
function _temp(e) {
  return e.stopPropagation();
}
function _temp2(e_0) {
  return e_0.stopPropagation();
}
function _temp3(voter) {
  return <Link key={voter.profile.handle ?? voter.profile.primary_address} href={`/${voter.profile.handle ?? voter.profile.primary_address}`} onClick={_temp2}>{voter.profile.pfp ? <img className="tw-h-6 tw-w-6 tw-rounded-md tw-border-2 tw-border-solid tw-border-[#111] tw-bg-iron-800 tw-object-contain" src={voter.profile.pfp} alt="Recent voter" /> : <div className="tw-h-6 tw-w-6 tw-rounded-lg tw-border-2 tw-border-solid tw-border-[#111] tw-bg-iron-800" />}</Link>;
}